import { FC, ReactNode } from "react";
import { useSiloWeb3 } from "../../SiloWeb3Provider";
import { twMerge } from "tailwind-merge";
import {
  ChainIdLegacy,
  LINKS,
  ProtocolInstance,
  ProtocolInstanceValue,
  RUNTIME_PROTOCOL_INSTANCES,
  SupportedChainValue,
  getProtocolInstanceChainId,
  protocolMap,
} from "@silo/types";
import { Button, ExternalLink, Logo, useDescriptionVariant } from "@silo/ui";

interface MenuItemContentProps {
  protocolInstance: ProtocolInstance;
  className?: string;
}

const MenuItemContent: FC<MenuItemContentProps> = ({
  protocolInstance,
  className,
}) => {
  const chainId = getProtocolInstanceChainId(protocolInstance);
  const src = CHAIN_ID_LOGO[chainId];
  const { secondaryLogo, label } =
    PROTOCOL_INSTANCE_MENU_CONFIG[protocolInstance];

  return (
    <div className={twMerge("flex flex-col gap-2.5", className)}>
      <div className="flex items-center gap-2.5">
        <Logo srcs={[src]} size="md" />

        {secondaryLogo && <span>{secondaryLogo}</span>}

        {label}
      </div>
    </div>
  );
};

export const ProtocolInstanceList: FC = () => {
  const { setProtocolInstance } = useSiloWeb3();

  return (
    <div className="flex flex-col gap-2.5">
      {RUNTIME_PROTOCOL_INSTANCES.map((_protocolInstance) => (
        <Button
          key={_protocolInstance}
          variant="quaternary"
          size="big"
          onClick={() => setProtocolInstance(_protocolInstance)}
        >
          <MenuItemContent
            protocolInstance={_protocolInstance}
            className="text-base font-normal"
          />
        </Button>
      ))}
    </div>
  );
};

export const CHAIN_ID_LOGO: SupportedChainValue<string> = {
  [ChainIdLegacy.ETHEREUM]: "/images/logos/ETH-light.png",
  [ChainIdLegacy.ARBITRUM]: "/images/networks/arbitrum_logo.svg",
  [ChainIdLegacy.OPTIMISM]: "/images/networks/optimism.svg",
  [ChainIdLegacy.BASE]: "/images/networks/base.svg",
  [ChainIdLegacy.LOCAL]: "/images/networks/hardhat-network.jpg",
  [ChainIdLegacy.ANVIL]: "/images/logos/ETH-light.png",
};

interface MenuConfig {
  label: ReactNode;
  secondaryLogo?: ReactNode;
  description?: ReactNode;
}

const MainnetLlamaDescription: FC = () => {
  const descriptionVariant = useDescriptionVariant();
  const beginningCopy = { static: "It", tooltip: "LLAMA edition" }[
    descriptionVariant
  ];

  /**
   * TODO: To show the link, we need to figure out why clicking it doesn't work. It's something related to how zag will automatically close the menu component whenever the mouse is pressed down outside of it.
   *
   * The tooltip being rendered in a Portal may also have something to do with it.
   */
  return (
    <div className="flex flex-col gap-5">
      <p>
        {beginningCopy} is a fork of Silo Finance protocol on Ethereum,
        featuring crvUSD as the bridge asset.
      </p>

      {false && descriptionVariant === "tooltip" && (
        <ExternalLink href={LINKS.SILOPEDIA.WHAT_IS_LLAMA} size="normal" />
      )}
    </div>
  );
};

export const LlamaLogo = () => (
  <Logo srcs={["/images/networks/llama.png"]} size="md" />
);

export const PROTOCOL_INSTANCE_MENU_CONFIG: ProtocolInstanceValue<MenuConfig> =
  {
    mainnetLegacy: {
      label: protocolMap.mainnetLegacy.label,
    },
    mainnetLlama: {
      label: protocolMap.mainnetLlama.label,
      secondaryLogo: <LlamaLogo />,
      description: <MainnetLlamaDescription />,
    },
    mainnetGrand: {
      label: protocolMap.mainnetGrand.label,
    },
    arbitrum: {
      label: protocolMap.arbitrum.label,
    },
    optimism: {
      label: protocolMap.optimism.label,
    },
    base: {
      label: protocolMap.base.label,
    },
    lrtbase: {
      label: protocolMap.lrtbase.label,
    },
    local: {
      label: protocolMap.local.label,
    },
    anvil: {
      label: protocolMap.anvil.label,
    },
  };
